import React, { useEffect, useState } from "react";
import {
    Box,
    Grid,
    Card,
    CardContent,
    CardMedia,
    Typography,
    Button,
    TextField,
    Chip,
    CircularProgress,
    Paper,
    Dialog,
} from "@mui/material";
import { useNavigate } from "react-router-dom";

const API_URL = window.location.href.includes("localhost")
    ? process.env.REACT_APP_API_URL_LOCAL
    : process.env.REACT_APP_API_URL_PROD;

const DeletedPropertyList = () => {
    const [properties, setProperties] = useState([]);
    const [filteredProperties, setFilteredProperties] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openVideo, setOpenVideo] = useState(false);
    const [videoUrl, setVideoUrl] = useState(null);
    const navigate = useNavigate();

    const [city, setCity] = useState("");

    useEffect(() => {
        fetchProperties();
    }, []);

    const getPropertyId = (property) =>
        typeof property._id === "object" ? property._id?.$oid : property._id;

    const fetchProperties = async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API_URL}api/get-properties-deleted`);
            const data = await res.json();
            const items = data.data || [];
            setProperties(items);
            setFilteredProperties(items);
        } catch (err) {
            console.error("Error fetching properties:", err);
        } finally {
            setLoading(false);
        }
    };

    // Restore API
    const restoreProperty = async (id) => {
        if (!window.confirm("Do you want to restore this property?")) return;

        try {
            const res = await fetch(`${API_URL}api/properties/restore/${id}`, {
                method: "POST",
            });
            const data = await res.json();

            if (data.success) {
                setProperties((prev) => prev.filter((p) => getPropertyId(p) !== id));
                setFilteredProperties((prev) => prev.filter((p) => getPropertyId(p) !== id));
                alert("Property restored successfully!");
            } else {
                alert("Failed to restore property");
            }
        } catch (err) {
            console.error("Restore failed:", err);
            alert("Error restoring property");
        }
    };

    const handleFilter = () => {
        let result = [...properties];
        if (city) {
            result = result.filter((p) =>
                p.city?.toLowerCase().includes(city.toLowerCase())
            );
        }
        setFilteredProperties(result);
    };

    const deleteProperty = async (id) => {
    if (!window.confirm("Are you sure you want to permanently delete this property?")) return;

    try {
        const res = await fetch(`${API_URL}api/properties/${id}`, {
            method: "DELETE",
        });
        const data = await res.json();
        if (data.success) {
            setProperties((prev) => prev.filter((p) => getPropertyId(p) !== id));
            setFilteredProperties((prev) => prev.filter((p) => getPropertyId(p) !== id));
            alert("Property deleted permanently!");
        } else {
            alert("Failed to delete property");
        }
    } catch (err) {
        console.error("Delete failed:", err);
        alert("Error deleting property");
    }
};


    const handleShowVideo = (property) => {
        if (!property.videoId) {
            alert("No video available for this property");
            return;
        }
        const url = `https://skyline-properties-media.s3.ap-south-1.amazonaws.com/${property.videoId}`;
        setVideoUrl(url);
        setOpenVideo(true);
    };

    return (
        <Box sx={{ p: 4, minHeight: "100vh", mt: 2 }}>
            <Typography variant="h4" sx={{ mb: 4 }}>
                Deleted Properties
            </Typography>

            {/* Filters */}
            <Paper
                elevation={6}
                sx={{
                    p: 3,
                    mb: 5,
                    borderRadius: 4,
                    display: "flex",
                    gap: 2,
                    flexWrap: "wrap",
                }}
            >
                <TextField
                    label="City / Location"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    sx={{ minWidth: 200 }}
                />

                <Button
                    variant="contained"
                    onClick={handleFilter}
                    sx={{
                        px: 4,
                        borderRadius: 3,
                        bgcolor: "purple",
                        fontWeight: "bold",
                        color: "white",
                    }}
                >
                    Search
                </Button>
            </Paper>

            {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", minHeight: "50vh" }}>
                    <CircularProgress size={70} sx={{ color: "purple" }} />
                </Box>
            ) : filteredProperties.length === 0 ? (
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "center",
                        minHeight: "40vh",
                        flexDirection: "column",
                        color: "gray",
                    }}
                >
                    <Typography variant="h5" sx={{ fontWeight: "bold", mb: 2 }}>
                        🚫 No Properties Found
                    </Typography>
                    <Typography>Try adjusting your filters or add a new property.</Typography>
                </Box>
            ) : (
                <Grid container spacing={3}>
                    {filteredProperties.map((property) => {
                        const id = getPropertyId(property);
                        const imageUrl = property.imageIds?.[0]
                            ? `https://skyline-properties-media.s3.ap-south-1.amazonaws.com/${property.imageIds[0]}`
                            : "/placeholder.jpg";

                        const chips = [
                            property.plotArea && `${property.plotArea} ${property.plotUnit}`,
                            property.carpetArea && `Carpet: ${property.carpetArea}`,
                            property.direction && `Dir: ${property.direction}`,
                            property.bhkCount && `${property.bhkCount} ${property.bhkType}`,
                        ].filter(Boolean);

                        return (
                            <Grid item xs={12} sm={6} md={4} lg={3} key={id}>
                                <Card
                                    sx={{
                                        borderRadius: 4,
                                        boxShadow: "0 8px 20px rgba(0,0,0,0.2)",
                                        overflow: "hidden",
                                    }}
                                >
                                    <CardMedia
                                        component="img"
                                        height="200"
                                        image={imageUrl}
                                        alt={property.name || "Property"}
                                    />
                                    <CardContent>
                                        <Typography
                                            variant="h6"
                                            sx={{ fontWeight: "bold", color: "#6a1b9a" }}
                                        >
                                            {property.rate
                                                ? `₹${property.rate} ${property.priceUnit || ""}`
                                                : property.rent
                                                ? `Rent: ₹${property.rent}`
                                                : "Price on Request"}
                                        </Typography>
                                        <Typography variant="subtitle1" fontWeight="bold">
                                            {property.name || "Unnamed Property"}
                                        </Typography>
                                        <Typography variant="body2" sx={{ mb: 1 }}>
                                            {[property.location, property.city].filter(Boolean).join(", ") || "No Location"}
                                        </Typography>

                                        {/* Tags */}
                                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
                                            {chips.map((chip, i) => (
                                                <Chip key={`${id}-chip-${i}`} label={chip} />
                                            ))}
                                        </Box>

                                        {/* Buttons */}
                                        {/* <Box sx={{ display: "flex", gap: 1 }}>
                                            <Button
                                                variant="contained"
                                                color="success"
                                                onClick={() => restoreProperty(id)}
                                                sx={{ borderRadius: 3, textTransform: "none" }}
                                            >
                                                Restore
                                            </Button>
                                            <Button
                                                variant="contained"
                                                color="error"
                                                onClick={() => deleteProperty(id)}
                                                sx={{ borderRadius: 3, textTransform: "none" }}
                                            >
                                                Delete Permanently
                                            </Button>
                                        </Box> */}
                                        <p style={{color:"red"}}>NOTE: You need to contact the developer in order to get this data back.</p>
                                    </CardContent>
                                </Card>
                            </Grid>
                        );
                    })}
                </Grid>
            )}

            {/* Video Dialog */}
            <Dialog open={openVideo} onClose={() => setOpenVideo(false)} maxWidth="md" fullWidth>
                <Box sx={{ p: 2 }}>
                    {videoUrl ? (
                        <video
                            src={videoUrl}
                            controls
                            autoPlay
                            style={{ width: "100%", borderRadius: 8 }}
                        />
                    ) : (
                        <Typography>No video available</Typography>
                    )}
                </Box>
            </Dialog>
        </Box>
    );
};

export default DeletedPropertyList;
